// src/client/components/About.jsx
import React, { useEffect, useMemo, useState } from "react";
import clientConfig from "../config/clientConfig";

const About = ({ theme }) => {
  const [aboutData, setAboutData] = useState(null);

  const texts = clientConfig.texts || {};
  const aboutTexts = texts.about || {};

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5050";

  useEffect(() => {
    fetch(`${API_URL}/api/about`)
      .then((res) => res.json())
      .then((data) => {
        const normalized = Array.isArray(data) ? data[0] : data;
        setAboutData(normalized?.description ? normalized : null);
      })
      .catch(() => setAboutData(null));
  }, [API_URL]);

  // Backend > fallback config
  const description = aboutData?.description || aboutTexts.description || "";
  const images = aboutData?.images?.length ? aboutData.images : aboutTexts.images || [];

  // config only
  const title = aboutTexts.title || "";
  const eyebrow = aboutTexts.eyebrow || "";
  const subtitle = aboutTexts.subtitle || "";

  const usps = useMemo(() => {
    const arr = Array.isArray(aboutTexts.usps) ? aboutTexts.usps : [];
    return arr.slice(0, 3);
  }, [aboutTexts.usps]);

  const trustItems = useMemo(() => {
    const arr = aboutTexts?.trust?.items;
    return Array.isArray(arr) ? arr.slice(0, 6) : [];
  }, [aboutTexts?.trust?.items]);

  const emptyGalleryText = aboutTexts.emptyGallery || "";

  const paragraphs = useMemo(() => {
    const raw = (description || "").trim();
    if (!raw) return [];
    const parts = raw.includes("\n\n") ? raw.split("\n\n") : [raw];
    return parts.map((p) => p.trim()).filter(Boolean);
  }, [description]);

  const c = (cls) => `${theme}-${cls}`;
  const sectionTitleId = `${theme}-about-title`;

  // ✅ Theme2 layout (signature premium night)
  if (theme === "theme2") {
    const uspsLabel = aboutTexts.uspsLabel || "";
    const trustLabel = aboutTexts.trustLabel || "";
    const galleryLabel = aboutTexts.galleryLabel || "";

    // pattern galerie : 1 grande + 2 petites + (optionnel) 2 autres petites
    const gallery = images.slice(0, 5);

    return (
      <section id="about" className="theme2-about" aria-labelledby={sectionTitleId}>
        <div className="theme2-about-bg" aria-hidden="true" />

        <div className="theme2-about-container">
          <header className="theme2-about-head">
            {eyebrow ? <p className="theme2-about-eyebrow">{eyebrow}</p> : null}

            <div className="theme2-about-titleRow">
              {title ? (
                <h2 id={sectionTitleId} className="theme2-section-title theme2-about-title">
                  {title}
                </h2>
              ) : null}
            </div>

            {subtitle ? <p className="theme2-about-subtitle">{subtitle}</p> : null}
          </header>

          <div className="theme2-about-grid">
            {/* LEFT: editorial copy */}
            <div className="theme2-about-left">
              {paragraphs.length ? (
                <div className="theme2-about-text">
                  {paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              ) : null}

              {/* USP timeline cards */}
              {usps.length ? (
                <div className="theme2-about-usps" aria-label={uspsLabel || undefined}>
                  {usps.map((u, idx) => (
                    <div key={idx} className="theme2-about-card">
                      <div className="theme2-about-card-rail" aria-hidden="true" />
                      {u.icon ? (
                        <div className="theme2-about-card-icon" aria-hidden="true">
                          {u.icon}
                        </div>
                      ) : null}

                      <div className="theme2-about-card-body">
                        {u.title ? <h3 className="theme2-about-card-title">{u.title}</h3> : null}
                        {u.desc ? <p className="theme2-about-card-desc">{u.desc}</p> : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* Trust pills */}
              {trustItems.length ? (
                <div className="theme2-about-trust" aria-label={trustLabel || undefined}>
                  {trustItems.map((t, i) => (
                    <span key={i} className="theme2-about-pill">
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            {/* RIGHT: asym gallery */}
            <aside className="theme2-about-right" aria-label={galleryLabel || undefined}>
              {gallery.length ? (
                <div className="theme2-about-gallery">
                  {/* big */}
                  {gallery[0] ? (
                    <figure className="theme2-about-fig theme2-about-fig--big">
                      <img
                        src={gallery[0]}
                        alt={title ? `${title} 1` : "Image 1"}
                        loading="lazy"
                        decoding="async"
                        className="theme2-about-img"
                        referrerPolicy="no-referrer"
                      />
                    </figure>
                  ) : null}

                  {/* small stack */}
                  <div className="theme2-about-figStack">
                    {gallery.slice(1, 3).map((img, i) => (
                      <figure key={img + i} className="theme2-about-fig theme2-about-fig--small">
                        <img
                          src={img}
                          alt={title ? `${title} ${i + 2}` : `Image ${i + 2}`}
                          loading="lazy"
                          decoding="async"
                          className="theme2-about-img"
                          referrerPolicy="no-referrer"
                        />
                      </figure>
                    ))}
                  </div>

                  {/* optional bottom row */}
                  {gallery.length > 3 ? (
                    <div className="theme2-about-figRow">
                      {gallery.slice(3, 5).map((img, i) => (
                        <figure key={img + i} className="theme2-about-fig theme2-about-fig--row">
                          <img
                            src={img}
                            alt={title ? `${title} ${i + 4}` : `Image ${i + 4}`}
                            loading="lazy"
                            decoding="async"
                            className="theme2-about-img"
                            referrerPolicy="no-referrer"
                          />
                        </figure>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : emptyGalleryText ? (
                <div className="theme2-about-empty">
                  <p>{emptyGalleryText}</p>
                </div>
              ) : null}
            </aside>
          </div>
        </div>
      </section>
    );
  }

  // ✅ Fallback: tes classes existantes (compat)
  return (
    <section id="about" className={`about-section ${c("about")}`}>
      <div className={c("about-bg")} aria-hidden="true" />

      <div className="container">
        <div className={c("about-head")}>
          {eyebrow ? <p className={c("about-eyebrow")}>{eyebrow}</p> : null}
          {title ? <h2 className={c("about-title")}>{title}</h2> : null}
          {subtitle ? <p className={c("about-subtitle")}>{subtitle}</p> : null}
        </div>

        <div className={c("about-grid")}>
          <div className={c("about-copy")}>
            {paragraphs.length ? (
              <div className={c("about-text")}>
                {paragraphs.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
            ) : null}

            {usps.length ? (
              <div className={c("about-usps")}>
                {usps.map((u, idx) => (
                  <div key={idx} className={c("about-card")}>
                    {u.icon ? (
                      <div className={c("about-card-icon")} aria-hidden="true">
                        {u.icon}
                      </div>
                    ) : null}
                    <div className={c("about-card-body")}>
                      {u.title ? <h3 className={c("about-card-title")}>{u.title}</h3> : null}
                      {u.desc ? <p className={c("about-card-desc")}>{u.desc}</p> : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {trustItems.length ? (
              <div className={c("about-trust")}>
                {trustItems.map((t, i) => (
                  <span key={i} className={c("about-trust-pill")}>
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className={c("about-gallery")}>
            {images.length ? (
              <div className={c("about-masonry")}>
                {images.slice(0, 6).map((img, i) => (
                  <figure key={i} className={c("about-fig")}>
                    <img
                      src={img}
                      alt={title ? `${title} ${i + 1}` : `Image ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      className={c("about-img")}
                      referrerPolicy="no-referrer"
                    />
                  </figure>
                ))}
              </div>
            ) : emptyGalleryText ? (
              <div className={c("about-empty")}>
                <p>{emptyGalleryText}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
